import React, { useState, useRef, useEffect, useCallback } from 'react';
import { mapsAPI } from '../../services/api';
import useGoogleMaps from '../../hooks/useGoogleMaps';
import useMapWithLocation from '../../hooks/useMapWithLocation';
import { X, ChevronRight, ChevronLeft, MapPin, FileText, CheckCircle, Trash2, Loader2, Globe, Lock } from 'lucide-react';
import './MapCreationModal.css';

const MapCreationModal = ({ isOpen, onClose, onMapCreated, eventId }) => {
  const mapRef = useRef(null);
  const { isLoaded, loadError } = useGoogleMaps();
  const [drawingManager, setDrawingManager] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [map, setMap] = useState(null);

  const mapInitializedRef = useRef(false);
  const selectedAreaRef = useRef(null);

  const {
    userLocation,
    locationError,
    isLoadingLocation,
    updateUserMarkerOnMap,
    cleanupMarkers,
    getMapConfig
  } = useMapWithLocation({
    enableTracking: isOpen,
    updateInterval: 3000,
    minDistanceChange: 3,
    showAccuracyCircle: true,
    highAccuracy: true
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    isPublic: true
  });

  const getDefaultDates = useCallback(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const formatDateTime = (date) => date.toISOString().slice(0, 16);
    return {
      start: formatDateTime(now),
      end: formatDateTime(tomorrow)
    };
  }, []);

  const calculateAreaData = useCallback((overlay, type) => {
    let coordinates = [];
    let bounds = null;
    let center = null;

    try {
      if (type === window.google.maps.drawing.OverlayType.RECTANGLE || type === 'rectangle') {
        const rectBounds = overlay.getBounds();
        const ne = rectBounds.getNorthEast();
        const sw = rectBounds.getSouthWest();

        coordinates = [
          [sw.lng(), sw.lat()],
          [ne.lng(), sw.lat()],
          [ne.lng(), ne.lat()],
          [sw.lng(), ne.lat()],
          [sw.lng(), sw.lat()]
        ];

        bounds = {
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng()
        };

        center = {
          lat: rectBounds.getCenter().lat(),
          lng: rectBounds.getCenter().lng()
        };
      } else if (type === window.google.maps.drawing.OverlayType.POLYGON || type === 'polygon') {
        const path = overlay.getPath();
        const bounds_obj = new window.google.maps.LatLngBounds();

        coordinates = [];
        path.forEach((latLng) => {
          coordinates.push([latLng.lng(), latLng.lat()]);
          bounds_obj.extend(latLng);
        });

        if (coordinates.length > 0) {
          coordinates.push(coordinates[0]);
        }

        const ne = bounds_obj.getNorthEast();
        const sw = bounds_obj.getSouthWest();

        bounds = {
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng()
        };

        center = {
          lat: bounds_obj.getCenter().lat(),
          lng: bounds_obj.getCenter().lng()
        };
      }

      return { type: 'Polygon', coordinates, bounds, center };
    } catch (error) {
      console.error('❌ Error calculating area data:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!isLoaded || !isOpen || !mapRef.current) return;
    if (mapInitializedRef.current) return;
    if (!userLocation) {
      console.log('⏳ Waiting for user location...');
      return;
    }

    try {
      mapInitializedRef.current = true;

      const mapConfig = getMapConfig(
        { lat: userLocation.lat, lng: userLocation.lng },
        16
      );

      const googleMap = new window.google.maps.Map(mapRef.current, mapConfig);
      setMap(googleMap);

      console.log('🗺️ Map initialized at user location (ONCE):', userLocation);

      if (!window.google.maps.drawing) {
        console.error('❌ Google Maps Drawing library not loaded');
        setError('Drawing tools not available. Please check your Google Maps API configuration.');
        return;
      }

      const drawing = new window.google.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: true,
        drawingControlOptions: {
          position: window.google.maps.ControlPosition.TOP_CENTER,
          drawingModes: [
            window.google.maps.drawing.OverlayType.RECTANGLE,
            window.google.maps.drawing.OverlayType.POLYGON
          ]
        },
        rectangleOptions: {
          strokeColor: '#2B8EE8',
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: '#2B8EE8',
          fillOpacity: 0.15,
          editable: true,
          draggable: true
        },
        polygonOptions: {
          strokeColor: '#2B8EE8',
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: '#2B8EE8',
          fillOpacity: 0.15,
          editable: true,
          draggable: true
        }
      });

      drawing.setMap(googleMap);
      setDrawingManager(drawing);

      drawing.addListener('overlaycomplete', (event) => {
        console.log('🎨 Area drawn:', event.type);

        if (selectedAreaRef.current) {
          selectedAreaRef.current.setMap(null);
        }

        const newArea = event.overlay;
        selectedAreaRef.current = newArea;
        setSelectedArea(newArea);

        drawing.setDrawingMode(null);

        const areaData = calculateAreaData(newArea, event.type);
        if (areaData) {
          console.log('📐 Area data calculated:', areaData);
        }
      });

      console.log('✅ Map and drawing tools initialized');
    } catch (error) {
      console.error('❌ Error initializing map:', error);
      mapInitializedRef.current = false;
      setError('Failed to initialize map. Make sure Google Maps API is loaded.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isOpen, userLocation]);

  useEffect(() => {
    if (!map || !userLocation) return;
    updateUserMarkerOnMap(map);
    console.log('📍 User location marker updated');
  }, [map, userLocation, updateUserMarkerOnMap]);

  useEffect(() => {
    if (!isOpen) {
      cleanupMarkers();
    }
  }, [isOpen, cleanupMarkers]);

  useEffect(() => {
    if (isOpen && !formData.startTime) {
      const dates = getDefaultDates();
      setFormData(prev => ({
        ...prev,
        startTime: dates.start,
        endTime: dates.end
      }));
    }
  }, [isOpen, formData.startTime, getDefaultDates]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const clearArea = useCallback(() => {
    if (selectedAreaRef.current) {
      selectedAreaRef.current.setMap(null);
      selectedAreaRef.current = null;
    }
    setSelectedArea(null);
    if (drawingManager) {
      drawingManager.setDrawingMode(null);
    }
    setStep(1);
  }, [drawingManager]);

  const nextStep = useCallback(() => {
    if (step === 1 && !selectedArea) {
      setError('Please draw an area on the map first');
      return;
    }
    if (step === 2 && (!formData.title || !formData.startTime || !formData.endTime)) {
      setError('Please fill in all required fields');
      return;
    }
    if (step === 2 && new Date(formData.startTime) >= new Date(formData.endTime)) {
      setError('End time must be after start time');
      return;
    }
    setError(null);
    setStep(step + 1);
  }, [step, selectedArea, formData]);

  const prevStep = useCallback(() => {
    setError(null);
    setStep(step - 1);
  }, [step]);

  const createMap = useCallback(async () => {
    if (!selectedAreaRef.current) {
      setError('No area selected');
      return;
    }

    try {
      setIsCreating(true);
      setError(null);

      const area = selectedAreaRef.current;
      const shapeType = (area.getBounds && typeof area.getBounds === 'function')
        ? 'rectangle'
        : 'polygon';

      const areaData = calculateAreaData(area, shapeType);

      if (!areaData) {
        setError('Failed to calculate area data');
        return;
      }

      const mapData = {
        title: formData.title,
        description: formData.description,
        area: areaData,
        startTime: formData.startTime,
        endTime: formData.endTime,
        isPublic: formData.isPublic,
        eventId: eventId || null,
      };

      console.log('🚀 Creating map with data:', mapData);

      const response = await mapsAPI.createMap(mapData);
      const createdMap = response.data.data.map;

      console.log('✅ Map created successfully:', createdMap);

      if (onMapCreated) {
        onMapCreated(createdMap);
      }

      clearArea();
      setFormData({ title: '', description: '', startTime: '', endTime: '', isPublic: true });
      setStep(1);
      setError(null);
      onClose();

    } catch (error) {
      console.error('❌ Error creating map:', error);
      setError(error.response?.data?.message || 'Failed to create map');
    } finally {
      setIsCreating(false);
    }
  }, [formData, calculateAreaData, onMapCreated, clearArea, onClose, eventId]);

  const handleClose = useCallback(() => {
    clearArea();
    cleanupMarkers();
    mapInitializedRef.current = false;
    setMap(null);
    setDrawingManager(null);
    setFormData({ title: '', description: '', startTime: '', endTime: '', isPublic: true });
    setStep(1);
    setError(null);
    setIsCreating(false);
    onClose();
  }, [clearArea, cleanupMarkers, onClose]);

  if (!isOpen) return null;

  if (loadError) {
    return (
      <div className="mcm-overlay">
        <div className="mcm-modal mcm-modal--compact">
          <div className="mcm-error-screen">
            <MapPin size={32} className="mcm-error-icon" />
            <h3>Map unavailable</h3>
            <p>{loadError}</p>
            <button onClick={handleClose} className="mcm-btn mcm-btn--primary">Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="mcm-overlay">
        <div className="mcm-modal mcm-modal--compact">
          <div className="mcm-loading-screen">
            <Loader2 size={32} className="mcm-spin" />
            <p>Loading map tools…</p>
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    { num: 1, label: 'Draw Area', icon: MapPin },
    { num: 2, label: 'Details', icon: FileText },
    { num: 3, label: 'Review', icon: CheckCircle },
  ];

  return (
    <div className="mcm-overlay" onClick={handleClose}>
      <div className="mcm-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="mcm-header">
          <div className="mcm-header-left">
            <h2 className="mcm-title">Create New Map</h2>
          </div>

          <div className="mcm-steps">
            {steps.map((s, i) => (
              <React.Fragment key={s.num}>
                <div className={`mcm-step ${step === s.num ? 'mcm-step--active' : ''} ${step > s.num ? 'mcm-step--done' : ''}`}>
                  <div className="mcm-step-circle">
                    {step > s.num ? <CheckCircle size={13} /> : <span>{s.num}</span>}
                  </div>
                  <span className="mcm-step-label">{s.label}</span>
                </div>
                {i < steps.length - 1 && <div className={`mcm-step-line ${step > s.num ? 'mcm-step-line--done' : ''}`} />}
              </React.Fragment>
            ))}
          </div>

          <button className="mcm-close-btn" onClick={handleClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="mcm-content">

          {/* Step 1 — Draw */}
          {step === 1 && (
            <div className="mcm-step-body">
              <div className="mcm-instructions">
                <p className="mcm-instructions-text">
                  Use the drawing tools on the map to outline your event area. Draw a <strong>rectangle</strong> for simple venues or a <strong>polygon</strong> for custom shapes.
                </p>
                <div className="mcm-tips">
                  {isLoadingLocation && (
                    <span className="mcm-tip mcm-tip--loading">
                      <Loader2 size={12} className="mcm-spin" /> Getting your location…
                    </span>
                  )}
                  {locationError && (
                    <span className="mcm-tip mcm-tip--warn">Location unavailable — using default</span>
                  )}
                  {userLocation?.accuracy && (
                    <span className="mcm-tip">GPS ±{Math.round(userLocation.accuracy)}m</span>
                  )}
                </div>
              </div>

              <div className="mcm-map-wrap">
                <div ref={mapRef} className="mcm-map" />
                {selectedArea && (
                  <button className="mcm-clear-btn" onClick={clearArea}>
                    <Trash2 size={13} /> Clear area
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 2 — Details */}
          {step === 2 && (
            <div className="mcm-step-body mcm-step-body--form">
              <div className="mcm-form">
                <div className="mcm-field">
                  <label className="mcm-label" htmlFor="title">Map title <span className="mcm-required">*</span></label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g. Summer Food Festival 2025"
                    maxLength={100}
                    className="mcm-input"
                  />
                </div>

                <div className="mcm-field">
                  <label className="mcm-label" htmlFor="description">Description <span className="mcm-optional">optional</span></label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe your event…"
                    maxLength={500}
                    rows={3}
                    className="mcm-textarea"
                  />
                </div>

                <div className="mcm-field-row">
                  <div className="mcm-field">
                    <label className="mcm-label" htmlFor="startTime">Start <span className="mcm-required">*</span></label>
                    <input
                      type="datetime-local"
                      id="startTime"
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleInputChange}
                      className="mcm-input"
                    />
                  </div>
                  <div className="mcm-field">
                    <label className="mcm-label" htmlFor="endTime">End <span className="mcm-required">*</span></label>
                    <input
                      type="datetime-local"
                      id="endTime"
                      name="endTime"
                      value={formData.endTime}
                      onChange={handleInputChange}
                      className="mcm-input"
                    />
                  </div>
                </div>

                <div className="mcm-visibility-toggle">
                  <div className="mcm-visibility-info">
                    {formData.isPublic
                      ? <Globe size={16} className="mcm-vis-icon mcm-vis-icon--public" />
                      : <Lock size={16} className="mcm-vis-icon mcm-vis-icon--private" />
                    }
                    <div>
                      <p className="mcm-vis-label">{formData.isPublic ? 'Public map' : 'Private map'}</p>
                      <p className="mcm-vis-help">
                        {formData.isPublic
                          ? 'Visitors can access via QR code or share link'
                          : 'Only you and collaborators can view this map'
                        }
                      </p>
                    </div>
                  </div>
                  <label className="mcm-switch">
                    <input
                      type="checkbox"
                      name="isPublic"
                      checked={formData.isPublic}
                      onChange={handleInputChange}
                    />
                    <span className="mcm-switch-track" />
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — Review */}
          {step === 3 && (
            <div className="mcm-step-body mcm-step-body--review">
              <div className="mcm-review-grid">
                <div className="mcm-review-card">
                  <p className="mcm-review-card-heading">Map details</p>
                  <div className="mcm-review-rows">
                    <div className="mcm-review-row">
                      <span className="mcm-review-key">Title</span>
                      <span className="mcm-review-val">{formData.title}</span>
                    </div>
                    {formData.description && (
                      <div className="mcm-review-row">
                        <span className="mcm-review-key">Description</span>
                        <span className="mcm-review-val">{formData.description}</span>
                      </div>
                    )}
                    <div className="mcm-review-row">
                      <span className="mcm-review-key">Start</span>
                      <span className="mcm-review-val">{new Date(formData.startTime).toLocaleString()}</span>
                    </div>
                    <div className="mcm-review-row">
                      <span className="mcm-review-key">End</span>
                      <span className="mcm-review-val">{new Date(formData.endTime).toLocaleString()}</span>
                    </div>
                    <div className="mcm-review-row">
                      <span className="mcm-review-key">Access</span>
                      <span className={`mcm-review-badge ${formData.isPublic ? 'mcm-review-badge--public' : 'mcm-review-badge--private'}`}>
                        {formData.isPublic ? <><Globe size={11} /> Public</> : <><Lock size={11} /> Private</>}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mcm-review-card mcm-review-card--area">
                  <p className="mcm-review-card-heading">Area</p>
                  <div className="mcm-area-ready">
                    <CheckCircle size={28} className="mcm-area-check" />
                    <p>Area defined and ready</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mcm-error-banner">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mcm-footer">
          <button onClick={handleClose} className="mcm-btn mcm-btn--ghost" disabled={isCreating}>
            Cancel
          </button>

          <div className="mcm-footer-right">
            {step > 1 && (
              <button onClick={prevStep} className="mcm-btn mcm-btn--secondary" disabled={isCreating}>
                <ChevronLeft size={15} /> Back
              </button>
            )}
            {step < 3 ? (
              <button onClick={nextStep} className="mcm-btn mcm-btn--primary">
                Next <ChevronRight size={15} />
              </button>
            ) : (
              <button onClick={createMap} className="mcm-btn mcm-btn--create" disabled={isCreating}>
                {isCreating
                  ? <><Loader2 size={15} className="mcm-spin" /> Creating…</>
                  : 'Create Map'
                }
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default MapCreationModal;